import {type RefObject, useCallback, useContext, useEffect, useId, useRef, useState} from "react";
import {Button} from "@mui/material";
import {useOverlayActivity} from "components/useOverlayActivity";
import {SheetTransition} from "transitions/SheetTransition";
import {overlay} from "theme/tokens";
import {type Locale, useLocale, useMessages} from "i18n";
import {ToastContext} from "Toast";
import {useUser} from "UserProvider";
import {formatMoney, MONTHLY} from "pricing/PricingStorage";
import {PricingDialog} from "pricing/PricingDialog";
import {PlanChangeDialog} from "pricing/PlanChangeDialog";
import {usePricingUpgrade} from "pricing/usePricingUpgrade";
import {CompactUpgradeDialog} from "upsell/CompactUpgradeDialog";
import {SubscriptionElementsCheckout} from "pay/SubscriptionElementsCheckout";
import {useEmbeddedSubscriptionCheckout} from "pay/useEmbeddedSubscriptionCheckout";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {CheckoutDocumentLink} from "pay/CheckoutDocumentLink";
import {useFirstMonthPlans} from "./useFirstMonthPlans";
import {claimDialogPrice, tierCardFigures, type FirstMonthPlanRow} from "./firstMonthPricing";
import type {OfferMoment} from "./offerEvents";
import {usePaidMoment} from "./usePaidMoment";
import {PlanIllustration} from "./PlanIllustration";
import {OfferOption} from "./OfferOption";
import {Options, OptionAsideNote} from "./OfferOption.styles";
import {useRadioGroupKeys} from "./useRadioGroupKeys";
import {
	Art, ArtCopy, ArtEyebrow, ArtPitch, ArtSceneBox, Content, Layout, UpgradeDialog,
} from "./CompactUpgradeDialog.styles";
import {
	Actions, CampaignFact, CampaignFacts, Continue, Failure, FullPricingLink, Later, Loading,
	PricingUnavailable, RetryPricing, Terms, Title,
} from "./FirstMonthPlansDialog.styles";
import {countdownDaysLabel} from "./campaignCountdown";
import {chooseStackedTier} from "./stackedTierChoice";
import {useCampaignCountdown} from "./useCampaignCountdown";
import {type PaidPlanId, type PlanId} from "pricing/planCatalogue";

/**
 * The tier picker behind the offer's Claim button (kitecorp/transferify-ui#1448).
 *
 * <b>Why a dialog at all.</b> The owner's call, 2026-08-26: claiming should upsell further rather than
 * drop the reader straight onto Stripe. The callout can name one tier; this can show what the same
 * discount buys on all three, which is the only place a reader who came for the cheapest plan ever
 * sees the others at half price.
 *
 * <b>Every figure here comes from the api.</b> The percentage is quoted per price by
 * `GET /pay/upsell-offer`, and the first-month amount is derived from it in the tested, React-free
 * `./firstMonthPricing`. A tier the api will not quote renders at its list price with no first-month
 * sentence — understating the offer rather than promising a discount Stripe will not apply. A quoted
 * tier states the first month and the renewal sentence, and nothing is struck through (ui#2456): a
 * struck list price would claim a reduction against a price that was not the lowest of the previous
 * 30 days once a welcome run has charged a first month below it.
 *
 * <b>AND A FAILED PRICING READ IS NOT A DEAD END (kitecorp/transferify-ui#1671).</b> Continue is
 * disabled on an empty tier list, so a failed `GET /plan` used to strand a reader who had just pressed
 * Claim, on every surface that opens this. `useFirstMonthPlans` now falls back to this bundle's own
 * baked amounts, which is why {@link percentOff} is a prop: the fallback needs the percentage the api
 * already quoted, and the surface that rendered the claim button is holding it. The fix lives here and
 * in the hook rather than at the four mounting points, so none of them can be fixed and forgotten.
 *
 * <b>Two checkout shapes, one verdict</b> (`pay/checkoutShape`, kitecorp/transferify-ui#2582 Part 2).
 * Every monthly purchase on every brand uses the in-place form: Continue swaps this dialog into its checkout state, where {@link pay/SubscriptionElementsCheckout}
 * renders the statutory block, the express consent and OUR order button over Stripe's Payment Element.
 * The consent tick is what mints the `ui_mode: elements` Session there — the api records the
 * acknowledgement on the creation request — which also closes the gap kitecorp/transferify-ui#2146 named:
 * this dialog collected no consent at all before.
 *
 * <b>It wears the compact upgrade dialog's shell</b> (owner, 2026-09-26): the same paper, the same
 * two columns and the same {@link ./PlanIllustration} on the left, collapsing to the same bottom sheet
 * with an artwork band. The art draws the LIT tier, so it follows the reader's pick; the tier picker,
 * the campaign facts and the actions are unchanged and sit in the right-hand column.
 */
export function FirstMonthPlansDialog(
	{open, moment, preselected, percentOff, occasion, endsOn, mustNotNavigate = false, onClaimed, onClose}: {
		open: boolean;
		/**
		 * The moment whose claim button opened this dialog (kitecorp/transferify-ui#1965), so the steps
		 * taken in here — the price seen, the checkout, the dismissal, the payment — are recorded on the same
		 * funnel row as the showing and the click that led to them. Passed by the surface because only the
		 * surface knows; four of them share this one dialog.
		 */
		moment: OfferMoment;
		preselected: PaidPlanId;
		/**
		 * The percentage the api quoted for the offer this dialog was opened from, or null when none is
		 * known. Read ONLY by the baked-catalogue fallback; a live read asks the api per tier.
		 */
		percentOff: number | null;
		/** The campaign's name key, or null when it is unnamed. */
		occasion: string | null;
		/** The campaign's last open day, or null when it never closes. */
		endsOn: string | null;
		/**
		 * The page behind this dialog must SURVIVE the purchase (kitecorp/transferify-ui#1665).
		 *
		 * True only where a full-page redirect would destroy something the reader cannot get back: the
		 * instant/P2P success screen, whose tab IS the live transfer. Continue then opens the in-place
		 * checkout INSIDE this dialog, whose order confirms without navigating.
		 *
		 * Off by default, so every other moment of kitecorp/transferify#117 keeps the hosted redirect it
		 * has always used — for a reader whose language allows it (`pay/checkoutShape`). The flag states
		 * the CONSTRAINT rather than the mechanism on purpose: the caller knows its page cannot be unloaded,
		 * and which Stripe shape satisfies that is this dialog's business.
		 */
		mustNotNavigate?: boolean;
		/**
		 * The purchase completed in this dialog, so the surface that opened it must stop offering.
		 *
		 * Only reachable on the in-place shape: the redirecting one never comes back. The plan bought does
		 * not switch on until the `invoice.paid` webhook lands, so without this the caller's own gate still
		 * reads a free account and keeps the offer on screen, one press away from a second subscription.
		 */
		onClaimed?: () => void;
		onClose: () => void;
	},
) {
	const {common, pricing, banner, checkout: checkoutCopy} = useMessages();
	const {locale} = useLocale();
	const [user] = useUser();
	const {rows, settled, spotsLeft, retry} = useFirstMonthPlans(open, percentOff);
	const contract = useCheckoutDocument(open);
	const inPage = useEmbeddedSubscriptionCheckout();
	// Pulled out so the reset effect below can depend on it without depending on the whole hook result,
	// which is a fresh object every render and would re-run the effect on every tick.
	const {dismiss: dismissPayment} = inPage;
	const {showSuccessToast} = useContext(ToastContext);
	// What BaseDialog used to own for this surface: the heading's id for the paper's accessible name, and
	// the exit hook focus restoration is wired to (see `components/useOverlayActivity`).
	const titleId = useId();
	const observedExit = useOverlayActivity(open);
	// No door of its own: every checkout here follows a press on the stacked table below, which names this
	// offer and its campaign as its host (kitecorp/transferify-ui#2914).
	const {onUpgrade, checkout, closeCheckout, planChange} = usePricingUpgrade(null);
	const [chosen, setChosen] = useState<PaidPlanId>(preselected);
	const [pricingOpen, setPricingOpen] = useState(false);
	// The in-place checkout state (Part 2): the tier list has been swapped for the elements form. Its own
	// state rather than "a session exists", because the form is on screen BEFORE the consent tick mints one.
	const [paying, setPaying] = useState(false);
	// Never seeded true and re-seeded false whenever the tier list comes back: a pre-ticked box is not
	// consent (§ 356 Abs. 5 Nr. 2 BGB, art. 16 lit. a) OUG 34/2014).
	const [consented, setConsented] = useState(false);
	// The paid-moment funnel's seam (kitecorp/transferify-ui#1965). This dialog is the tier picker the
	// six offer moments share, so the showing and the click are the SURFACE'S to report (`useOfferEvents`,
	// hence `reportsShown: false` — a second OFFER_SHOWN here would inflate every one of those six
	// denominators by one per Claim) and the steps from here on are reported against the moment it hands
	// in. The figure seen is the lit tier's, a React-free rule, computed once per render for the three
	// reporters below; null until the rows have priced, so a dialog still loading reports no price.
	const litTierPrice = claimDialogPrice(rows, chosen);
	const paidMoment = usePaidMoment(moment, {
		visible: open,
		price: litTierPrice,
		occasion,
		reportsShown: false,
	});
	// Re-seeded whenever the dialog is opened rather than only on mount: BaseDialog keeps its subtree
	// alive across a close/open cycle, so a reader who picked Premium, closed and reopened would find
	// Premium still selected — a choice they did not make this time. The nested pricing dialog is also
	// reset on close so reopening Claim always returns to this offer, never the table visited last time.
	//
	// The in-place checkout is reset with it (ui#1665). Its session belongs to the opening that minted it,
	// so a dialog closed from OUTSIDE — the offer unmounting, the phase moving on — must not reopen
	// straight into an abandoned Stripe form, into a ticked box, or into the failure message from a
	// previous attempt.
	useEffect(() => {
		if (open) {
			setChosen(preselected);
		} else {
			setPricingOpen(false);
			setPaying(false);
			setConsented(false);
			dismissPayment();
		}
	}, [open, preselected, dismissPayment]);
	const offerCopy = banner.firstMonthOffer;
	const copy = offerCopy.dialog;
	// Only ticks while the dialog is open: a closed dialog keeps its subtree mounted, and a timer behind
	// it would re-render every minute for a surface nobody is looking at.
	const countdown = useCampaignCountdown(open ? endsOn : null);
	// An occasion the api sends that this build has no copy for falls back to the plain title, so a
	// campaign minted in the dashboard after this deploy renders unnamed rather than blank.
	const title = (occasion === null ? null : offerCopy.occasions[occasion]) ?? copy.title;
	// The lit tier's own row: what the in-place checkout's block states as the recurring price, and
	// whether it sells a discounted first month at all.
	const chosenRow = rows.find(row => row.plan === chosen);
	// The tiers are one radio group of the exit offer's rows (owner, 2026-09-27): the checked row owns
	// the only tab stop, so the arrow keys are how the reader reaches the other two.
	const tierGroup = useRadioGroupKeys(rows.map(row => row.plan), chosen, setChosen);

	// Continue only opens the form; consent is collected before minting, and our button places the order.
	const claim = () => { if (chosenRow !== undefined) setPaying(true); };

	/**
	 * The consent tick mints; the untick abandons (Part 2). The document read is taken AFTER the tick
	 * rather than captured at render, for the reason `useCheckoutDocument.latestRead` gives.
	 *
	 * The checkout step is NOT reported here. This callback is also what the order form's explicit Retry
	 * calls, so reporting on every invocation counted one checkout per retry — the same over-count
	 * `useCompactUpgrade` avoids. It is reported once per MINTED session in the effect below instead.
	 */
	const onConsentChange = useCallback((checked: boolean) => {
		setConsented(checked);
		if (!checked) {
			dismissPayment();
			return;
		}
		const documentSelection = contract.latestRead();
		void inPage.start(chosen, MONTHLY, {
			uiMode: "elements",
			immediatePerformanceConsent: true,
			...(documentSelection ? {documentSelection} : {}),
		});
	}, [chosen, contract, dismissPayment, inPage]);

	/**
	 * One `CHECKOUT_OPENED` per minted Session, keyed on the secret exactly as `useCompactUpgrade` keys it.
	 *
	 * A Retry that mints afresh is a new checkout and reports one; a Retry that fails to mint reports
	 * nothing, because no checkout opened. Reporting from the tick instead counted a retry as a second
	 * checkout on the same offer, which is the denominator `upsell_offer_events` divides by.
	 */
	const reportedSecret = useRef<string | null>(null);
	useEffect(() => {
		const secret = inPage.clientSecret;
		if (secret === null || reportedSecret.current === secret) {
			return;
		}
		reportedSecret.current = secret;
		paidMoment.reportCheckoutOpened(litTierPrice);
	}, [inPage.clientSecret, litTierPrice, paidMoment]);

	// Paid, but not yet entitled: the plan is switched on by the `invoice.paid` webhook moments later,
	// so the reader is told it is being confirmed rather than shown a plan the next read might not
	// agree with. The dialog closes, because the offer it exists to sell has been taken.
	const paid = () => {
		paidMoment.reportPaid(litTierPrice);
		inPage.completed();
		// Before the toast and the close, because both are about telling the reader; this is about not
		// selling them the same subscription twice while Stripe's webhook is still in flight.
		onClaimed?.();
		showSuccessToast(copy.checkout.confirming);
		onClose();
	};

	// Later, the ✕ and Escape on the tier list are the reader walking away from the offer. Closing the
	// in-place checkout is NOT: that returns them to the tier list, still inside the offer.
	const later = () => {
		paidMoment.reportDismissed();
		onClose();
	};

	// Escape and Back mean "abandon the payment" while the checkout is up and "abandon the offer"
	// otherwise. One dialog with two states rather than two dialogs, which is what keeps focus
	// restoration unambiguous: there is ONE trap and ONE opener (the offer's Claim button), so every
	// exit lands on a control that is still mounted. Stacking a second dialog gave the inner trap an
	// opener that unmounts on the completed path, which is the detached-node case
	// `dialog-focus-ownership` exists to prevent.
	/**
	 * The full table's CTA on the non-navigating path: adopt the tier and come back here.
	 *
	 * `find` rather than a cast — the table can name the free tier, which this dialog cannot select, and
	 * matching against the paid ids narrows the type without asserting anything.
	 */
	const chooseWithoutLeaving = (planId: PlanId) => {
		const paidTier = chooseStackedTier(planId);
		if (paidTier !== undefined) {
			setChosen(paidTier);
		}
		setPricingOpen(false);
	};

	const dismiss = () => {
		if (paying) {
			// Back to the tier list, still inside the offer: the session (if any) is abandoned and the box
			// comes back unticked, so a second Continue meets a fresh form rather than a remembered tick.
			inPage.dismiss();
			setConsented(false);
			setPaying(false);
			return;
		}
		later();
	};

	return (
		<>
			<UpgradeDialog
				open={open}
				onClose={(_event: object, reason: string) => {
					// A card number lives in a cross-origin Stripe iframe, so a stray click on the scrim
					// would throw it away with no way to recover it. Only while the checkout is up: the tier
					// list has nothing to lose, and this app's dialogs are otherwise scrim-dismissable.
					// Escape still closes, and the Back action below keeps that reachable on a touch device.
					if (reason === "backdropClick" && paying) {
						return;
					}
					dismiss();
				}}
				maxWidth={false}
				slots={{transition: SheetTransition}}
				slotProps={{
					transition: {onExited: observedExit},
					backdrop: {style: {background: overlay.scrim}},
				}}
				aria-labelledby={titleId}
			>
				<Layout>
					<Art>
						<ArtSceneBox>
							<PlanIllustration plan={chosen}/>
						</ArtSceneBox>
						<ArtCopy>
							<ArtEyebrow>{pricing.planNames[chosen]}</ArtEyebrow>
							{/* The lit tier's own lead bullet, as the compact dialog's panel shows it: catalogue
								copy, never a line written for this surface. */}
							<ArtPitch>{pricing.features[chosen][0]}</ArtPitch>
						</ArtCopy>
					</Art>
					<Content>
						<Title id={titleId}>{paying ? copy.checkout.title : title}</Title>
						{paying && chosenRow !== undefined ? (
							<>
								{/* ABOVE the form, never under it: the order button inside the form must be the last
									control on this surface (OLG Celle 13 U 21/26). */}
								<Button onClick={dismiss}>{common.back}</Button>
								<SubscriptionElementsCheckout
									plan={chosen}
									clientSecret={inPage.clientSecret}
									collectsShippingAddress={inPage.collectsShippingAddress}
									contractDocument={contract.document}
									awaitingDocument={contract.awaitingDocument}
									consented={consented}
									buyerEmail={user.email === "" ? undefined : user.email}
									onConsentChange={onConsentChange}
									minting={inPage.busy}
									// The tier's RECURRING price, which is what the block states; the discounted first
									// month is passed as `firstCharge`, which the order button names (ui#3394).
									monthlyPrice={formatMoney(chosenRow.currency, chosenRow.monthlyAmount, locale)}
									// The offer's own terms sentence beside the recurring price, only where this tier
									// actually sells a discounted first month: an unquoted tier is bought at list price
									// and must not be told a discount applies.
									introTerms={chosenRow.firstMonthAmount === null ? undefined : copy.terms}
									// The order button names the discounted first month where there is one (ui#3394).
									firstCharge={chosenRow.firstMonthAmount === null ? undefined
										: formatMoney(chosenRow.currency, chosenRow.firstMonthAmount, locale)}
									mintRefusal={inPage.error === null ? null : checkoutCopy.errors[inPage.error]}
									onPlaced={paid}
								/>
							</>
						) : (
							<>
								{(spotsLeft !== null || countdown !== null) && (
									<CampaignFacts>
										{spotsLeft !== null && (
											<CampaignFact data-testid="first-month-spots-left">
												{offerCopy.spotsLeft(spotsLeft)}
											</CampaignFact>
										)}
										{/*
										  * The SAME inclusive-days sentence the success card and the callouts render
										  * (ui#1672). This dialog is opened FROM a pill that already states the
										  * deadline, so a finer unit here adds no precision — it tells one reader, in
										  * one click, both that the offer has three days left and that it has
										  * two-and-a-bit days left. One deadline gets one rendering.
										  */}
										{countdown !== null && (
											<CampaignFact data-testid="first-month-deadline">
												{offerCopy.endsIn(countdownDaysLabel(countdown, offerCopy))}
											</CampaignFact>
										)}
									</CampaignFacts>
								)}
								{/*
								  * THREE EXACT STATES, and the third is the one ui#1671 was filed about. Still
								  * asking and priced are unchanged. Settled with nothing used to render an empty
								  * radiogroup above a disabled Continue and say nothing at all; the baked-catalogue
								  * fallback makes that unreachable for any bundle carrying prices, and where it IS
								  * reached the reader gets a sentence and a control rather than a dialog that
								  * cannot be acted on.
								  */}
								{rows.length > 0 ? (
									<Options role="radiogroup" aria-label={copy.title} onKeyDown={tierGroup.onKeyDown}>
										{rows.map(row => (
											<TierRow
												key={row.plan}
												row={row}
												name={pricing.planNames[row.plan]}
												locale={locale}
												welcome={pricing.welcome}
												selected={row.plan === chosen}
												selectedRef={tierGroup.selectedRef}
												onSelect={() => setChosen(row.plan)}
											/>
										))}
									</Options>
								) : settled ? (
									<PricingUnavailable>
										<Failure role="alert">{copy.pricingUnavailable}</Failure>
										<RetryPricing type="button" onClick={retry}>{common.retry}</RetryPricing>
									</PricingUnavailable>
								) : (
									<Loading>{copy.loading}</Loading>
								)}
								<Terms>{copy.terms}</Terms>
								{/* After the renewal note it qualifies (owner, 2026-09-27), and still above the order controls. */}
								<CheckoutDocumentLink document={contract.document}/>
								<Actions>
									{/* The complete table stacks above this offer (ui#1637). Closing it restores the
										same claim choice instead of navigating away and discarding this flow. */}
									<FullPricingLink type="button" onClick={() => setPricingOpen(true)}>
										{copy.fullPricing}
									</FullPricingLink>
									<Later type="button" onClick={later}>{copy.later}</Later>
									{/*
									  * The primary names no tier since ui#1665 (owner, 2026-08-30): the lit tier card
									  * above it already does, and repeating the name made this the widest control in
									  * a row that has to hold three of them at 311px.
									  *
									  * The empty-list clause stays, and it is no longer a dead end (ui#1671): there is
									  * genuinely no tier to buy when nothing priced, and that branch now renders the
									  * failure and its retry above rather than this button on its own. What it can no
									  * longer mean is "the pricing read failed" — the baked catalogue answers that.
									  *
									  * "Weiter" in German, not the statutory wording: this press opens the checkout,
									  * and the press that concludes the order is the one inside it.
									  */}
									<Continue
										type="button"
										disabled={rows.length === 0 || contract.awaitingDocument}
										onClick={claim}
									>
										{copy.continue}
									</Continue>
								</Actions>
							</>
						)}
					</Content>
				</Layout>
			</UpgradeDialog>
			{/*
			  * THE FULL TABLE IS A CHOOSER HERE, NOT A SECOND CHECKOUT (review of ui#1736).
			  *
			  * `usePricingUpgrade` routes a signed-in free reader to the checkout dialog, whose hosted shape's
			  * `CheckoutRedirect` sets `window.location.href` once the buyer ticks — so wiring the table's CTA to it
			  * put a full-page redirect two clicks from a live P2P transfer, which is the one outcome
			  * `mustNotNavigate` exists to forbid and the precondition `upsell/firstMonthOffer` leans on
			  * to offer instant senders anything at all.
			  *
			  * On that path the table therefore answers back into THIS dialog: picking a tier selects it
			  * above and closes the table, and the two navigating surfaces below are not rendered at all.
			  * Not rendering them is the enforcement — a redirect cannot be reached from a subtree that
			  * does not exist — where threading a flag into each would leave the next one to remember.
			  */}
			{/* No exit offer above this one (ui#1722): closing the table returns the reader to the offer
				dialog they opened it from, which is still on screen behind it. */}
			<PricingDialog
				open={pricingOpen}
				onClose={() => setPricingOpen(false)}
				onUpgrade={mustNotNavigate ? chooseWithoutLeaving : onUpgrade}
				suppressExitOffer
				host={mustNotNavigate ? null : {moment, occasion}}
			/>
			{!mustNotNavigate && (
				<>
					<CompactUpgradeDialog checkout={checkout} onClose={closeCheckout}/>
					<PlanChangeDialog {...planChange}/>
				</>
			)}
		</>
	);
}

/**
 * One tier, as one row of the exit offer's list (owner, 2026-09-27): the plan name and the first-month
 * price on the row, the renewal sentence as its smaller second line. The figures come from the
 * React-free `tierCardFigures`, so the quoted/plain branch is stated once: a tier the api did not
 * quote shows its list price and no second line. Both row amounts are already MAJOR units; the
 * minor-unit conversion happens once, inside `firstMonthMajor`, and is not repeated here.
 */
function TierRow(
	{row, name, locale, welcome, selected, selectedRef, onSelect}: {
		row: FirstMonthPlanRow;
		name: string;
		/** Any shipped locale: it only reaches `tierCardFigures`, which hands it to Intl. */
		locale: Locale;
		/** The welcome-price copy the pricing sheet uses for the same presentation: the label and the renewal sentence. */
		welcome: {firstMonth: string; thenMonthly: (list: string) => string};
		selected: boolean;
		selectedRef: RefObject<HTMLButtonElement | null>;
		onSelect: () => void;
	},
) {
	const figures = tierCardFigures(row, locale, welcome.thenMonthly);
	return (
		<OfferOption
			selected={selected}
			onSelect={onSelect}
			selectedRef={selectedRef}
			testId={`first-month-tier-${row.plan}`}
			title={name}
			aside={figures.kind === "list"
				? figures.amount
				: <><OptionAsideNote>{welcome.firstMonth}</OptionAsideNote>{figures.amount}</>}
			detail={figures.kind === "list" ? undefined : figures.note}
		/>
	);
}
